import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import axiosInstance from '../../../../axios/axiosInstance';
import { Grade } from 'model/response';
import React, { useEffect, useState } from 'react';
import { Input, message, Popconfirm, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import GradeFormModal from './GradeFormModal';
import { ModePage } from 'common/enum';
import processError from '../../../../utils/processError';
import { findAllGrade, toggleGradeWebviewActive } from 'service/gradeService';
import { setGrades } from '../../../../redux/slice/masterDataSlice';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../redux/store/store';
import WebViewActiveCheckbox from '../../../common/WebViewActiveCheckbox';
import useTableConfig from '../../../../hooks/useTableConfig';

export default function GradeManagement() {
  const { pageSize, currentPage, sortBy, direction, pagination, setTotalElements, getColumnSorter, handleTableChange } =
    useTableConfig();
  const [keyword, setKeyword] = useState<string>('');
  const [gradeId, setGradeId] = useState<string>('');
  const [mode, setMode] = useState<ModePage>(ModePage.ADD);
  const [isOpen, setOpenModal] = useState<boolean>(false);

  const [data, setData] = useState<Grade[]>();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const columns: ColumnsType<Grade> = [
    {
      title: 'Tên',
      dataIndex: 'name',
      width: '40%',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('name'),
    },
    {
      title: 'Mã',
      dataIndex: 'code',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('code'),
    },
    {
      title: 'Hiển thị',
      dataIndex: 'webViewActive',
      align: 'center',
      render: (_, record) => (
        <WebViewActiveCheckbox
          currentActiveValue={record.webViewActive}
          itemName={'lớp'}
          setLoading={setLoading}
          toggleWebviewActive={async () => toggleGradeWebviewActive(record.id)}
          reloadPageData={loadListGrades}
        />
      ),
      sorter: true,
      sortOrder: getColumnSorter('webViewActive'),
    },
    {
      title: 'Thứ tự',
      dataIndex: 'webViewOrder',
      align: 'center',
      sorter: true,
      sortOrder: getColumnSorter('webViewOrder'),
    },
    {
      title: 'Hành động',
      dataIndex: 'action',
      align: 'center',
      render: (_, record) => (
        <div className="flex item-center w-full justify-center gap-2">
          <button className="p-2 rounded bg-[#cdf1ff]" onClick={(e) => moveEditView(e, record)}>
            <EditOutlined className="" />
          </button>
          <Popconfirm
            title="Xóa lớp"
            description="Xác nhận xóa lớp. Hành động này không thể hoàn tác?"
            onConfirm={() => deleteGrade(record)}
            okText="Đồng ý"
            cancelText="Hủy"
          >
            <button className="p-2 rounded bg-red-500">
              <DeleteOutlined />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const deleteGrade = async (record: Grade) => {
    setLoading(true);
    try {
      await axiosInstance({
        url: `grades/${record.id}`,
        method: 'delete',
      });
      message.success('Xoá thành công.');
      await loadListGrades();
    } catch (err) {
      processError(err);
    }
    setLoading(false);
  };

  const moveEditView = async (e: any, record: Grade) => {
    setMode(ModePage.EDIT);
    setOpenModal(true);
    setGradeId(record.id);
  };

  const loadListGrades = async function () {
    setLoading(true);
    const res = await axiosInstance({
      url: 'grades/page',
      params: {
        keyword,
        size: pageSize,
        page: currentPage,
        sortBy,
        direction,
        gradeId: gradeId,
        fromCMS: true,
      },
    });

    if (res.status === 200) {
      setData(res.data.content);
      setLoading(false);
      setTotalElements(res.data.totalElements);
    }
  };

  const onSearch = function (e: any) {
    setKeyword(e.target.value);
  };

  const addNewGrade = async () => {
    setMode(ModePage.ADD);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleBeforeSuccess = () => {
    setOpenModal(false);
    loadListGrades().then();
    findAllGrade()
      .then((grades) => {
        dispatch(setGrades(grades));
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    loadListGrades().then();
  }, [currentPage, pageSize, sortBy, direction, keyword]);

  return (
    <div className="w-full h-[calc(100vh-112px)] overflow-y-auto p-4">
      <div className="p-2 flex items-center justify-between bg-white rounded">
        <div className="flex gap-4">
          <button
            className="py-1 bg-[#cdf1ff] hover:bg-[#bbe7fa] flex items-center rounded px-2"
            onClick={() => addNewGrade()}
          >
            <PlusOutlined className="text-[15px]" />
            <span className="pl-2">Thêm mới</span>
          </button>
        </div>
        <Input placeholder="Nhập từ khóa tìm kiếm" name="keyword" className="w-fit" onPressEnter={onSearch} />
      </div>
      <div className="w-full bg-white mt-4 rounded p-2 overflow-y-auto h-[calc(100%-60px)]">
        <Table
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      <GradeFormModal
        isOpen={isOpen}
        onCloseModal={handleCloseModal}
        mode={mode}
        onBeforeSuccess={handleBeforeSuccess}
        gradeId={gradeId}
      />
    </div>
  );
}
